import React, { useEffect, useState, createContext, useContext, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { useGetWorkspacePageQuery } from '@/features/workspaces/workspacesApi';

const initialState = {
	dealId: null,
	clientId: null,
	draftHtml: '',
	savedHtml: '',
	isUnsaved: false,
	isBusy: false,
	handleChange: (newHtml) => {},
	handleSaved: () => {},
	currentFilePath: '',
};

const WorkspaceContext = createContext(initialState);

const WorkspaceContextProvider = ({ children }) => {
	const params = useParams();

	// A deal page shows the deal's workspace, a company page the company's, otherwise the client's.
	const dealId = params.dealId;
	const relationshipCompanyId = dealId ? undefined : params.relationshipCompanyId;
	const clientId = dealId || relationshipCompanyId ? undefined : params.clientId;
	const objectType = dealId ? 'deal' : relationshipCompanyId ? 'relationshipCompany' : 'client';

	const [draftHtml, setDraftHtml] = useState(null);

	useEffect(() => {
		setDraftHtml(null);
	}, [dealId, clientId, relationshipCompanyId]);

	const {
		currentData: workspaceData,
		isFetching,
		isUninitialized,
	} = useGetWorkspacePageQuery(
		{ dealId, clientId, relationshipCompanyId },
		{ skip: !dealId && !clientId && !relationshipCompanyId },
	);

	const savedHtml = workspaceData?.html ?? '';

	const parsedHtml = useMemo(() => {
		if (!draftHtml) return null;
		if (draftHtml === '<p style="line-height: 1.5;"></p>') return null;
		return draftHtml;
	}, [draftHtml]);

	const isUnsaved = parsedHtml !== null && parsedHtml !== savedHtml;
	const isBusy = isUninitialized || (isFetching && workspaceData === undefined);

	const currentFilePath = workspaceData?.filePath;

	const handleChange = (html) => {
		if (isBusy) return;
		setDraftHtml(html);
	};

	const handleSaved = () => {
		setDraftHtml(null);
	};

	const value = {
		dealId,
		clientId,
		relationshipCompanyId,
		objectType,
		draftHtml,
		savedHtml,
		isUnsaved,
		isBusy,
		handleChange,
		handleSaved,
		currentFilePath,
	};

	return <WorkspaceContext.Provider value={value}>{children}</WorkspaceContext.Provider>;
};

export const useWorkspaceContext = () => {
	const context = useContext(WorkspaceContext);
	if (!context) {
		throw new Error('useWorkspaceContext must be used within a WorkspaceContextProvider');
	}

	return context;
};

export default WorkspaceContextProvider;
